import React, { useCallback } from "react";
import { useHistory } from "react-router-dom";
import { AlertTriangle, RefreshCw } from "lucide-react";
import CONFIG from "config/config";
import { Button } from "components/ui/button";

export interface BuilderErrorStateProps {
  // Re-attempts the failed campaign load. Most builder-load failures are transient,
  // so retry recovers the common case; harmless on the deterministic ones.
  onRetry?: () => void;
  title?: string;
  description?: string;
}

// Interim generic full-screen error for the redesigned builder (PAD-31535). One
// state for every fatal load/render failure across channels — no per-failure-type
// variants. Replaces the bare /error redirect and the endless ADM skeleton. Renders
// inside the builder's content area, so the sidebar stays. Final design TBD.
const BuilderErrorState = ({
  onRetry,
  title = "Something went wrong on our end",
  description = "A temporary problem stopped this page from loading. Trying again usually fixes it.",
}: BuilderErrorStateProps) => {
  const history = useHistory();

  // Return to wherever the user came from. Falls back to the dashboard when there's
  // no prior in-app entry (direct open / fresh tab), where back would dead-end.
  const handleGoBack = useCallback(() => {
    if (window.history.length > 1) {
      history.goBack();
    } else {
      window.location.href = `${CONFIG.PLATFORM_BASE_URL}/dashboard/home`;
    }
  }, [history]);

  return (
    <div
      className="cb-v2-root flex h-full w-full flex-col items-center justify-center gap-6 bg-page px-6 text-center"
      data-cypress-id="builder-error-state"
    >
      <div className="flex size-16 items-center justify-center rounded-2xl bg-primary/10">
        <AlertTriangle className="size-7 text-primary" />
      </div>
      <div className="flex flex-col gap-2">
        <h1 className="text-2xl font-bold text-foreground">{title}</h1>
        <p className="max-w-md text-sm text-muted-foreground">{description}</p>
      </div>
      <div className="flex flex-col items-center gap-2">
        {onRetry && (
          <Button type="button" onClick={onRetry} data-cypress-id="builder-error-retry">
            <RefreshCw />
            Try again
          </Button>
        )}
        <Button type="button" variant="ghost" onClick={handleGoBack} data-cypress-id="builder-error-back">
          Go back
        </Button>
      </div>
    </div>
  );
};

export { BuilderErrorState };
